import React, { useMemo } from "react";
import { Play, Pause, SkipBack, SkipForward, Rewind, FastForward, Volume2, ListMusic, X, Trash2, Gauge } from "lucide-react";
import { usePlayer, fmtTime } from "../context/PlayerContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Link } from "react-router-dom";

export default function PlayerDock() {
  const {
    current, isPlaying, toggle, next, prev, progress, duration, seek, skip,
    volume, setVolume, queue, currentIndex, queueOpen, setQueueOpen, playIndex, removeFromQueue, clearQueue,
    rate, setRate, RATES,
  } = usePlayer();

  const pct = useMemo(() => (duration ? (progress / duration) * 100 : 0), [progress, duration]);

  const onScrub = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    seek(ratio * duration);
  };

  return (
    <>
      <div className="fixed left-0 right-0 bottom-0 z-40 pb-[env(safe-area-inset-bottom)]" data-testid="player-dock">
        <div className="mx-auto max-w-[1600px]">
          <div className="glass-card border-t rounded-none lg:mx-4 lg:mb-4 lg:rounded-lg overflow-hidden">
            {/* progress hair */}
            <div
              className="progress-track !h-[3px] !rounded-none"
              onClick={onScrub}
              data-testid="player-progress"
              role="slider"
              aria-valuenow={Math.round(pct)}
              aria-label="Playback progress"
            >
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>

            <div className="flex items-center gap-2 lg:gap-4 px-3 lg:px-6 py-3 min-w-0">
              {/* left: track meta */}
              <div className="flex items-center gap-3 min-w-0 flex-1 lg:w-1/3 lg:flex-none">
                <div className="w-12 h-12 shrink-0 rounded-md overflow-hidden bg-black/40 border border-white/5">
                  {current?.thumbnail && (
                    <img src={current.thumbnail} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-[15px] font-display truncate text-paper" data-testid="player-title">
                    {current?.title || "Nothing playing"}
                  </div>
                  <Link
                    to={current?.parent ? `/item/${encodeURIComponent(current.parent)}` : "#"}
                    className="text-xs text-white/50 truncate block hover:text-emerald-soft"
                  >
                    {current?.creator || "Select a lecture to begin"}
                  </Link>
                </div>
              </div>

              {/* center: controls */}
              <div className="hidden lg:flex flex-col items-center flex-1 max-w-md mx-auto">
                <div className="flex items-center gap-3">
                  <button onClick={prev} aria-label="Previous" data-testid="player-prev" className="p-2 text-white/70 hover:text-paper transition">
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button onClick={() => skip(-10)} aria-label="Rewind 10s" data-testid="player-rewind" className="p-2 text-white/70 hover:text-paper transition">
                    <Rewind className="w-4 h-4" />
                  </button>
                  <button onClick={toggle} data-testid="player-play-toggle" className="play-btn" aria-label={isPlaying ? "Pause" : "Play"}>
                    {isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 ml-0.5 fill-current" />}
                  </button>
                  <button onClick={() => skip(10)} aria-label="Forward 10s" data-testid="player-forward" className="p-2 text-white/70 hover:text-paper transition">
                    <FastForward className="w-4 h-4" />
                  </button>
                  <button onClick={next} aria-label="Next" data-testid="player-next" className="p-2 text-white/70 hover:text-paper transition">
                    <SkipForward className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-white/50 tabular-nums">
                  <span data-testid="player-progress-time">{fmtTime(progress)}</span>
                  <span>/</span>
                  <span data-testid="player-duration-time">{fmtTime(duration)}</span>
                </div>
              </div>

              {/* right: speed + volume + queue */}
              <div className="flex items-center gap-2 lg:gap-3 shrink-0 lg:w-1/3 lg:justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="chip"
                      data-testid="player-speed-btn"
                      aria-label="Playback speed"
                      title="Playback speed"
                    >
                      <Gauge className="w-4 h-4" />
                      <span className="tabular-nums">{rate}x</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-[#161009] border border-white/10 text-paper min-w-[120px]"
                  >
                    {RATES.map(r => (
                      <DropdownMenuItem
                        key={r}
                        onSelect={() => setRate(r)}
                        data-testid={`player-speed-${r}`}
                        className={`cursor-pointer justify-between focus:bg-white/5 ${
                          r === rate ? "text-emerald-soft" : "text-white/80"
                        }`}
                      >
                        <span>{r}x</span>
                        {r === rate && <span className="w-1.5 h-1.5 rounded-full bg-emerald-soft" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="hidden md:flex items-center gap-2 text-white/60">
                  <Volume2 className="w-4 h-4" />
                  <input
                    type="range" min="0" max="1" step="0.01"
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="accent-emerald-glow w-24"
                    data-testid="player-volume"
                  />
                </div>
                <button
                  onClick={() => setQueueOpen(true)}
                  className="chip"
                  data-testid="player-open-queue"
                >
                  <ListMusic className="w-4 h-4" /> <span className="hidden sm:inline">Queue </span>({queue.length})
                </button>
                {/* mobile play button */}
                <button onClick={toggle} className="lg:hidden play-btn" aria-label="Play/Pause" data-testid="player-play-toggle-mobile">
                  {isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 ml-0.5 fill-current" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Sheet open={queueOpen} onOpenChange={setQueueOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#110d0a] border-l border-white/5 text-paper">
          <SheetHeader className="mb-4">
            <SheetTitle className="font-display text-2xl text-paper flex items-center gap-2">
              <ListMusic className="w-5 h-5 text-emerald-soft" /> Queue
            </SheetTitle>
          </SheetHeader>

          <div className="flex items-center justify-between mb-3">
            <p className="label-caps">{queue.length} track{queue.length !== 1 && "s"}</p>
            {queue.length > 0 && (
              <button onClick={clearQueue} className="text-xs text-white/60 hover:text-destructive flex items-center gap-1" data-testid="queue-clear">
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>

          <div className="flex flex-col gap-2 overflow-y-auto max-h-[calc(100vh-160px)] pr-1" data-testid="queue-list">
            {queue.length === 0 && (
              <p className="text-white/50 text-sm py-10 text-center">
                Your queue is empty. Add lectures from anywhere in the archive.
              </p>
            )}
            {queue.map((t, i) => (
              <div
                key={`${t.id}-${i}`}
                className={`flex items-center gap-3 p-2 rounded-md border transition ${
                  i === currentIndex ? "border-emerald-glow/60 bg-emerald-glow/10" : "border-white/5 hover:border-white/10"
                }`}
                data-testid={`queue-item-${i}`}
              >
                <button
                  onClick={() => playIndex(i)}
                  className="w-10 h-10 shrink-0 rounded-md overflow-hidden bg-black/40 relative group"
                  aria-label="Play this track"
                >
                  <img src={t.thumbnail} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100" />
                  <span className="absolute inset-0 flex items-center justify-center text-paper">
                    <Play className="w-4 h-4 fill-current" />
                  </span>
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-display truncate">{t.title}</div>
                  <div className="text-xs text-white/50 truncate">{t.creator}</div>
                </div>
                <button
                  onClick={() => removeFromQueue(i)}
                  className="p-1.5 text-white/50 hover:text-destructive"
                  aria-label="Remove"
                  data-testid={`queue-remove-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
